/* RESPONSIBLE TEAM: team-inbox-experience */
import { type RenderableData } from 'embercom/objects/inbox/renderable-data';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import UserSummary, { type UserSummaryWireFormat } from 'embercom/objects/inbox/user-summary';
import { EntityType } from 'embercom/models/data/entity-types';
import Tag, { type TagWireFormat } from 'embercom/objects/inbox/tag';
import { type Channel } from 'embercom/models/data/inbox/channels';
import { tracked } from '@glimmer/tracking';
import { Upload, type UploadWireFormat } from 'embercom/objects/inbox/renderable/upload';

interface MergedUserCommentWireFormat {
  renderable_type: RenderableType;
  blocks: Array<object>;
  user_summary: UserSummaryWireFormat;
  tags: Array<TagWireFormat>;
  channel: Channel;
  url?: string | null;
  ua?: string | null;
  uploads?: Array<UploadWireFormat>;
  from_conversation_id: number;
}

export default class MergedUserComment implements RenderableData {
  readonly renderableType = RenderableType.MergedUserComment;
  readonly blocks: Array<object>;
  readonly userSummary: UserSummary;
  @tracked tags: Array<Tag>;
  readonly channel: Channel;
  readonly url?: string;
  readonly ua?: string;
  readonly uploads?: Array<Upload>;
  readonly fromConversationId: number;

  get creatingEntity() {
    return { type: EntityType.User, id: this.userSummary.id, object: this.userSummary };
  }

  get styles() {
    return {
      backgroundColor: 'bg-neutral-container',
      textColor: 'text-default',
      backgroundColorHover: 'bg-neutral-container-emphasis',
    };
  }

  constructor(
    blocks: Array<object>,
    userSummary: UserSummary,
    tags: Array<Tag>,
    channel: Channel,
    fromConversationId: number,
    url?: string,
    ua?: string,
    uploads?: Array<Upload>,
  ) {
    this.blocks = blocks;
    this.userSummary = userSummary;
    this.tags = tags || [];
    this.channel = channel;
    this.fromConversationId = fromConversationId;
    this.url = url;
    this.ua = ua;
    this.uploads = uploads || [];
  }

  static deserialize(json: MergedUserCommentWireFormat): MergedUserComment {
    let userSummary = UserSummary.deserialize(json.user_summary);
    return new MergedUserComment(
      json.blocks,
      userSummary,
      json.tags && json.tags.map(Tag.deserialize),
      json.channel,
      json.from_conversation_id,
      json.url || undefined,
      json.ua || undefined,
      json.uploads?.map(Upload.deserialize),
    );
  }
}
